import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Target, CheckCircle } from 'lucide-react';
import { getBundleById } from '../../data/prioritization-selector/bundles';

const BundleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const bundle = id ? getBundleById(id) : null;

  if (!bundle) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] mb-4">
          Bundle Not Found
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          The requested bundle could not be found.
        </p>
        <button
          onClick={() => navigate('..')}
          className="btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'low':
        return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]';
      case 'medium':
        return 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
      case 'high':
        return 'text-[var(--coral)] bg-[var(--coral-dim)]';
      default:
        return 'text-[var(--text-sec)] bg-[var(--hover-tint)]';
    }
  };

  const getTimeIcon = (timeRequired: string) => {
    switch (timeRequired) {
      case 'minutes':
        return <Clock className="w-4 h-4" />;
      case 'hours':
        return <Clock className="w-4 h-4" />;
      case 'days':
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getTeamSizeIcon = (teamSize: string) => {
    switch (teamSize) {
      case 'individual':
        return <Target className="w-4 h-4" />;
      case 'small':
        return <Users className="w-4 h-4" />;
      case 'medium':
        return <Users className="w-4 h-4" />;
      case 'large':
        return <Users className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate('..')}
          className="flex items-center text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Decision Tree
        </button>
        
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-display font-bold text-[var(--text-pri)] mb-2">
                {bundle.name}
              </h1>
              <p className="text-xl text-[var(--text-sec)] mb-4">
                {bundle.description}
              </p>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getComplexityColor(bundle.complexity)}`}>
                  {bundle.complexity} complexity
                </span>
                <span className="flex items-center text-sm text-[var(--text-sec)]">
                  {getTimeIcon(bundle.timeRequired)}
                  <span className="ml-1">{bundle.timeRequired}</span>
                </span>
                <span className="flex items-center text-sm text-[var(--text-sec)]">
                  {getTeamSizeIcon(bundle.teamSize)}
                  <span className="ml-1">{bundle.teamSize}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Plain Language Question
            </h2>
            <div className="bg-[var(--slate-dim)] p-4">
              <p className="text-[var(--text-sec)] font-medium">{bundle.plainLanguageQuestion}</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Use Case
            </h2>
            <p className="text-[var(--text-sec)] text-lg">{bundle.useCase}</p>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Tools Included
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {bundle.tools.map((tool, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--surface)]">
                  <CheckCircle className="w-5 h-5 text-[var(--green)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--text-sec)]">{tool}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Process Steps
            </h2>
            <ol className="space-y-4">
              {bundle.process.map((step, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full flex items-center justify-center text-sm font-semibold mr-4 mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-[var(--text-sec)] text-lg">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Expected Output
            </h2>
            <div className="bg-[var(--green-dim)] p-4">
              <p className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">{bundle.output}</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Scenario
            </h2>
            <p className="text-[var(--text-sec)] text-lg">{bundle.scenario}</p>
          </div>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate('..')}
          className="btn-primary"
        >
          Start Your Analysis
        </button>
      </div>
    </div>
  );
};

export default BundleDetails;

